import { useState } from "react";
import { toast } from "sonner";
import {
  Plug,
  Copy,
  Check,
  Zap,
  Search,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";

const MCP_URL = "https://mcp.krunsh.io/mcp";

function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = (value: string, key: string, label = "Copié") => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    toast.success(label);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
  };
  return { copiedKey, copy };
}

function CopyButton({
  value,
  k,
  label,
  size = "sm",
}: {
  value: string;
  k: string;
  label: string;
  size?: "sm" | "default";
  variant?: "outline" | "default" | "secondary";
}) {
  const { copiedKey, copy } = useCopy();
  const active = copiedKey === k;
  return (
    <Button
      variant="secondaryFilled"
      size={size}
      onClick={() => copy(value, k)}
      className="gap-2"
    >
      {active ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {active ? "Copié" : label}
    </Button>
  );
}

function GuidePanel({
  logo,
  title,
  subtitle,
  steps,
  exampleLabel,
  examplePrompt,
  exampleResult,
  microNote,
}: {
  logo: string;
  title: string;
  subtitle: string;
  steps: { title: string; text: string }[];
  exampleLabel: string;
  examplePrompt: string;
  exampleResult: string;
  microNote?: string;

  copyKeyPrefix: string;
}) {
  return (
    <Card className="border-border overflow-hidden animate-in fade-in-50 duration-200">
      <div className="p-5 sm:p-6 border-b border-border flex items-start gap-4">
        <div
          className={`h-11 w-11 rounded-xl bg-[white] text-white flex items-center justify-center text-lg font-semibold shrink-0`}
        >
          <img src={logo} alt={title} className="object-contain" />
        </div>
        <div className="min-w-0">
          <div className="text-base sm:text-lg font-semibold text-foreground">
            {title}
          </div>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>
      <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[1.4fr_1fr] lg:gap-8">
        <div>
          <ol className="space-y-4">
            {steps.map((s, i) => (
              <li key={s.title} className="flex gap-4">
                <div className="h-7 w-7 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                  {i + 1}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {s.title}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">
                    {s.text}
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {microNote && (
            <p className="mt-5 text-xs text-muted-foreground italic">
              {microNote}
            </p>
          )}
        </div>
        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-muted/30 p-4">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
              {exampleLabel}
            </div>
            <div className="mt-2 font-mono text-sm text-foreground leading-snug">
              {examplePrompt}
            </div>
            <div className="mt-2 text-xs text-muted-foreground leading-relaxed">
              {exampleResult}
            </div>
          </div>
          <div className="flex flex-col gap-2"></div>
        </div>
      </div>
    </Card>
  );
}

const connectors = [
  {
    id: "claude",
    name: "Claude",
    badge: "Disponible",
    badgeVariant: "default" as const,
    description:
      "Connectez Krunsh MCP à Claude pour poser des questions avancées sur Uber Eats, Deliveroo, vos enseignes, vos grilles et vos opportunités marché.",
    capabilities: [
      "Recherche de restaurants",
      "Comparaison d'enseignes",
      "Détails restaurant",
      "Grilles",
      "Favoris",
      "Historique & signaux",
    ],
    status: "Remote MCP",
    logo: "/svg/claude-logo.svg",
    logoBg: "white",
    installUrl: "https://claude.ai/settings/connectors",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    badge: "Disponible selon workspace",
    badgeVariant: "secondary" as const,
    description:
      "Ajoutez Krunsh comme connecteur MCP dans ChatGPT pour interroger vos données marché directement depuis vos conversations et workflows.",
    capabilities: [
      "Recherche conversationnelle",
      "Benchmarks",
      "Analyse concurrentielle",
      "Exports via KC",
      "Actions MCP",
      "Automatisations",
    ],
    status: "Remote MCP",
    logo: "/svg/chatgpt-logo.svg",
    logoBg: "white",
    installUrl: "https://chatgpt.com/",
    note: "La disponibilité peut dépendre du plan et des paramètres workspace.",
  },
];

const claudeSteps = [
  {
    title: "Ouvrez Claude",
    text: "Accédez à Claude ou Claude Desktop avec un compte compatible connecteurs.",
  },
  {
    title: "Allez dans Connectors",
    text: "Ouvrez les paramètres, puis la section Connectors.",
  },
  {
    title: "Ajoutez un connecteur personnalisé",
    text: "Cliquez sur Add custom connector, puis renseignez l'URL MCP Krunsh.",
  },
  {
    title: "Authentifiez Krunsh",
    text: "Collez votre token Krunsh ou suivez le flux d'authentification proposé.",
  },
  {
    title: "Testez une première requête",
    text: "Posez une question comme : « Compare KFC, Burger King et O'Tacos à Lyon. »",
  },
];

const chatgptSteps = [
  {
    title: "Ouvrez les paramètres workspace",
    text: "Les connecteurs MCP personnalisés peuvent nécessiter un workspace compatible et le mode développeur activé.",
  },
  {
    title: "Activez les apps / connecteurs MCP",
    text: "Dans les paramètres workspace, activez les options liées aux apps personnalisées ou aux connecteurs MCP.",
  },
  {
    title: "Ajoutez Krunsh MCP",
    text: "Créez une app ou un connecteur personnalisé et renseignez l'URL du serveur MCP Krunsh.",
  },
  {
    title: "Définissez les permissions",
    text: "Autorisez les tools Krunsh nécessaires : recherche, détails restaurant, grilles, favoris, exports et historique.",
  },
  {
    title: "Testez dans une conversation",
    text: "Demandez à ChatGPT d'analyser une enseigne, une zone ou une comparaison concurrentielle.",
  },
];

const examplePrompts = [
  {
    category: "Benchmark",
    prompt: "Quels sont les leaders burger sur Uber Eats à Paris ?",
    tool: "search-restaurants",
  },
  {
    category: "Comparaison",
    prompt: "Compare KFC, Burger King et O'Tacos sur Deliveroo à Lyon.",
    tool: "createGrid",
  },
  {
    category: "Monitoring",
    prompt: "Quelles enseignes ont les meilleurs délais à Marseille ?",
    tool: "search-restaurants",
  },
  {
    category: "Expansion",
    prompt: "Où ouvrir une marque healthy à Bordeaux ?",
    tool: "search-restaurants",
  },
  {
    category: "Monitoring",
    prompt: "Quels nouveaux concurrents sont apparus dans ma zone ?",
    tool: "get-restaurant-history",
  },
  {
    category: "Grid",
    prompt: "Crée une grille comparative entre mes restaurants suivis.",
    tool: "createGrid",
  },
  {
    category: "Favoris",
    prompt: "Ajoute ces restaurants à mes favoris.",
    tool: "addRestaurantsToFavoriteList",
  },
  {
    category: "Historique",
    prompt: "Analyse l'évolution des promotions de Pokawa.",
    tool: "get-restaurant-history",
  },
];

export default function ConnectorsPage() {
  const { copiedKey, copy } = useCopy();
  const { sm } = useDetectSizeDevice();

  const scrollTo = (id: string) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      style={{
        height: sm
          ? "calc(100dvh - var(--height-header))"
          : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
      }}
      className="mx-auto max-w-[1280px] px-4 sm:px-6 pt-5 sm:pt-7 pb-12 space-y-8 overflow-auto"
    >
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-50/60 via-white to-white p-6 sm:p-10">
        <div className="absolute inset-0 -z-0 opacity-40 [background:radial-gradient(circle_at_20%_10%,hsl(var(--primary)/0.12),transparent_40%),radial-gradient(circle_at_80%_60%,hsl(var(--primary)/0.08),transparent_45%)]" />
        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/80 px-3 py-1 text-xs font-medium tracking-wide text-muted-foreground">
            <Plug className="h-3.5 w-3.5" />• CONNECTEURS
          </div>
          <h1 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Connectez Krunsh à vos outils IA.
          </h1>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground">
            Utilisez Krunsh MCP dans Claude, ChatGPT et vos agents IA pour
            rechercher, comparer et analyser le marché de la livraison en
            langage naturel.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Badge variant="secondary" className="rounded-full">
              Claude
            </Badge>
            <Badge variant="secondary" className="rounded-full">
              ChatGPT
            </Badge>
            <Badge variant="secondary" className="rounded-full">
              MCP
            </Badge>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => scrollTo("connection")} className="gap-2">
              <Zap className="h-4 w-4" /> Configurer un connecteur
            </Button>
            <Button
              onClick={() => scrollTo("guides")}
              variant="secondaryFilled"
              className="gap-2"
            >
              Voir les guides d'installation
            </Button>
          </div>
        </div>
      </section>

      {/* Connecteurs disponibles */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Connecteurs disponibles
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Deux façons d'utiliser Krunsh MCP dans vos assistants IA préférés.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {connectors.map((c) => (
            <Card key={c.id} className="p-6 flex flex-col gap-5 border-border">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`h-11 w-11 rounded-xl ${c.logoBg} text-white flex items-center justify-center text-lg font-semibold`}
                  >
                    <img src={c.logo} alt={c.name} className="object-contain" />
                  </div>
                  <div>
                    <div className="text-base font-semibold text-foreground">
                      {c.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {c.status}
                    </div>
                  </div>
                </div>
                <Badge variant={c.badgeVariant} className="rounded-full">
                  {c.badge}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {c.description}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {c.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="text-xs px-2 py-1 rounded-md bg-muted text-foreground/80 border border-border"
                  >
                    {cap}
                  </span>
                ))}
              </div>
              {c.note && (
                <p className="text-xs text-muted-foreground italic">{c.note}</p>
              )}
              <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                <Button variant="secondaryFilled" asChild className="gap-2">
                  <a
                    href={c.installUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#44546F" }}
                  >
                    Installer sur {c.name}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
                <CopyButton
                  value={MCP_URL}
                  k={`mcp-${c.id}`}
                  label="Copier l'URL MCP"
                />
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Connexion MCP */}
      <section id="connection" className="space-y-4">
        <div className="flex items-end justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">
              Votre connexion MCP
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Informations à renseigner dans votre assistant IA.
            </p>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 rounded-full">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block" />
            Actif
          </Badge>
        </div>

        <Card className="p-6 space-y-5 border-border">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                URL du serveur MCP
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 font-mono text-sm">
                <span className="flex-1 truncate">{MCP_URL}</span>
                <button
                  onClick={() => copy(MCP_URL, "url")}
                  className="text-muted-foreground hover:text-foreground p-1 rounded"
                  aria-label="Copier l'URL"
                >
                  {copiedKey === "url" ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Guides */}
      <section id="guides" className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Guides d'installation
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choisissez votre assistant IA et suivez les étapes pour connecter
            Krunsh MCP.
          </p>
        </div>

        <Tabs defaultValue="claude" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 sm:w-auto sm:inline-grid">
            <TabsTrigger value="claude" className="gap-2">
              <img
                src="/svg/claude-logo.svg"
                alt="Claude"
                className="h-[20px] w-[20px] object-contain"
              />
              Claude
            </TabsTrigger>
            <TabsTrigger value="chatgpt" className="gap-2">
              <img
                src="/svg/chatgpt-logo.svg"
                alt="ChatGPT"
                className="h-[20px] w-[20px] object-contain"
              />
              ChatGPT
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="claude"
            className="mt-4 focus-visible:outline-none"
          >
            <GuidePanel
              logo="/svg/claude-logo.svg"
              title="Installer Krunsh dans Claude"
              subtitle="Ajoutez Krunsh MCP comme connecteur pour interroger le marché de la livraison directement depuis Claude."
              steps={claudeSteps}
              exampleLabel="Exemple Claude"
              examplePrompt="Compare KFC, Burger King et O'Tacos à Lyon."
              exampleResult="Claude appelle Krunsh MCP et retourne un benchmark structuré."
              copyKeyPrefix="claude"
            />
          </TabsContent>

          <TabsContent
            value="chatgpt"
            className="mt-4 focus-visible:outline-none"
          >
            <GuidePanel
              logo="/svg/chatgpt-logo.svg"
              title="Installer Krunsh dans ChatGPT"
              subtitle="Ajoutez Krunsh MCP à ChatGPT pour utiliser les tools Krunsh directement depuis vos conversations et workflows."
              steps={chatgptSteps}
              exampleLabel="Exemple ChatGPT"
              examplePrompt="Où ouvrir une marque healthy à Marseille ?"
              exampleResult="ChatGPT utilise Krunsh MCP pour récupérer les données marché et structurer une recommandation."
              microNote="Les intitulés exacts peuvent varier selon le plan ChatGPT et les paramètres administrateur."
              copyKeyPrefix="chatgpt"
            />
          </TabsContent>
        </Tabs>
      </section>

      {/* Exemples de requêtes */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Ce que vous pouvez demander à Krunsh MCP
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Cliquez sur un exemple pour le copier dans votre presse-papier.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {examplePrompts.map((p) => (
            <button
              key={p.prompt}
              onClick={() =>
                copy(p.prompt, `prompt-${p.prompt}`, "Prompt copié")
              }
              className="text-left p-4 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition group"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <Badge className="text-xs rounded-full">{p.category}</Badge>
                <Copy className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition" />
              </div>
              <div className="text-sm text-foreground leading-snug">
                {p.prompt}
              </div>
              <div className="mt-3 text-[11px] font-mono text-muted-foreground">
                → {p.tool}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* KC / permissions */}
      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            Connecteurs, permissions et Krunsh Credits
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Les connecteurs utilisent vos permissions Krunsh et peuvent
            consommer des Krunsh Credits selon les actions lancées.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Card className="p-5 border-border">
            <Search className="h-5 w-5 text-primary mb-3" />
            <div className="font-semibold text-foreground">Recherche</div>
            <p className="text-sm text-muted-foreground mt-1.5">
              Les requêtes simples permettent de retrouver des restaurants,
              enseignes ou zones.
            </p>
          </Card>
          <Card className="p-5 border-border">
            <Sparkles className="h-5 w-5 text-primary mb-3" />
            <div className="font-semibold text-foreground">
              Actions avancées
            </div>
            <p className="text-sm text-muted-foreground mt-1.5">
              Les exports, enrichissements, API, MCP et automatisations peuvent
              consommer des KC.
            </p>
          </Card>
          <Card className="p-5 border-border bg-gradient-to-br from-primary/5 to-transparent">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="h-5 w-5 text-primary" />
              <Badge className="rounded-full">Pro</Badge>
            </div>
            <div className="font-semibold text-foreground">Plan Pro</div>
            <p className="text-sm text-muted-foreground mt-1.5">
              Pro débloque l'usage complet de la plateforme et un quota mensuel
              de KC plus élevé.
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
}
